import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Eye, Route } from "lucide-react";
import HistorialEntregas, { ArchivosEntrega, RESULTADO_CLS } from "@/components/HistorialEntregas";
import { fechaMX, fechaHoraMX } from "@/lib/format";

const EST_CLS = { Programado: "bg-slate-100 text-slate-700", "En ruta": "bg-amber-100 text-amber-700", Completado: "bg-emerald-100 text-emerald-700" };
const hoy = () => new Date().toLocaleDateString("en-CA");

function ViajeDialog({ cur, setCur, choferes, camiones, onSaved }) {
  const [disp, setDisp] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (cur) api.get("/viajes/remisiones-disponibles").then(({ data }) => setDisp(data || [])); }, [cur?.id, !!cur]); // eslint-disable-line react-hooks/exhaustive-deps
  const opciones = useMemo(() => {
    const propias = (cur?.pendientes || []).map((e) => ({ id: e.remision_id, numero_remision: e.numero_remision, cliente: e.cliente, numero_pedido: e.numero_pedido, direccion: e.direccion, kg: Number(e.kg || 0) }));
    return [...propias, ...disp.filter((d) => !propias.some((p) => p.id === d.id))];
  }, [disp, cur]);
  if (!cur) return null;
  const sel = new Set(cur.remision_ids);
  const toggle = (id) => setCur({ ...cur, remision_ids: sel.has(id) ? cur.remision_ids.filter((x) => x !== id) : [...cur.remision_ids, id] });
  const kg = opciones.filter((o) => sel.has(o.id)).reduce((a, o) => a + Number(o.kg || 0), 0);
  const cap = Number(camiones.find((c) => c.id === cur.camion_id)?.capacidad_kg || 0);
  const guardar = async () => {
    if (!cur.chofer_id) return toast.error("Elige el chofer");
    if (!cur.remision_ids.length && !cur.hechas) return toast.error("Agrega al menos una remisión");
    setSaving(true);
    const body = { chofer_id: cur.chofer_id, camion_id: cur.camion_id || null, fecha: cur.fecha, notas: cur.notas || null, remision_ids: [...cur.remision_ids, ...(cur.hechasIds || [])] };
    try {
      if (cur.id) await api.put(`/viajes/${cur.id}`, body); else await api.post("/viajes", body);
      toast.success("Viaje guardado"); setCur(null); onSaved();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && setCur(null)}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="viaje-dialog">
        <DialogHeader><DialogTitle>{cur.id ? `Editar viaje ${cur.folio}` : "Nuevo viaje"}</DialogTitle><DialogDescription>Elige chofer, camión y las remisiones que van en este flete.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Chofer *</Label>
            <Select value={cur.chofer_id} onValueChange={(v) => setCur({ ...cur, chofer_id: v })} disabled={!!cur.hechas}>
              <SelectTrigger data-testid="viaje-chofer"><SelectValue placeholder="Elige chofer" /></SelectTrigger>
              <SelectContent>{choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
            </Select>
            {choferes.length === 0 && <p className="text-xs text-red-600 mt-1">Da de alta un chofer en Configuración → Usuarios y Permisos.</p>}
          </div>
          <div><Label>Camión</Label>
            <Select value={cur.camion_id || "ninguno"} onValueChange={(v) => setCur({ ...cur, camion_id: v === "ninguno" ? "" : v })}>
              <SelectTrigger data-testid="viaje-camion"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ninguno">Sin camión</SelectItem>{camiones.filter((c) => c.estatus === "Activo").map((c) => <SelectItem key={c.id} value={c.id}>{c.placas} {c.marca || ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fecha</Label><Input type="date" value={cur.fecha} onChange={(e) => setCur({ ...cur, fecha: e.target.value })} data-testid="viaje-fecha" /></div>
        </div>
        <div>
          <div className="flex items-center justify-between"><Label>Remisiones ({cur.remision_ids.length})</Label>
            <span className={`text-xs ${cap && kg > cap ? "text-red-600 font-semibold" : "text-muted-foreground"}`} data-testid="viaje-kg">{kg.toLocaleString("es-MX")} kg{cap ? ` de ${cap.toLocaleString("es-MX")} kg` : ""}</span>
          </div>
          {cur.hechas > 0 && <p className="text-xs text-muted-foreground">{cur.hechas} entrega(s) ya registradas se conservan.</p>}
          <div className="mt-1 border rounded-md divide-y max-h-72 overflow-y-auto" data-testid="viaje-remisiones">
            {opciones.length === 0 && <p className="p-3 text-sm text-muted-foreground">No hay remisiones pendientes de entregar.</p>}
            {opciones.map((o) => (
              <label key={o.id} className="flex items-start gap-3 p-2.5 cursor-pointer hover:bg-muted/40" data-testid={`viaje-rem-${o.id}`}>
                <Checkbox checked={sel.has(o.id)} onCheckedChange={() => toggle(o.id)} className="mt-0.5" />
                <div className="text-sm flex-1"><span className="font-semibold">{o.numero_remision}</span>{o.tipo === "complemento" && <Badge className="ml-1 bg-amber-100 text-amber-800 border-transparent">Complemento</Badge>} · {o.cliente}{o.numero_pedido && <span className="text-muted-foreground"> · {o.numero_pedido}</span>}
                  <div className="text-xs text-muted-foreground">{o.direccion || "Sin dirección"} · {Number(o.kg || 0).toLocaleString("es-MX")} kg</div></div>
              </label>
            ))}
          </div>
        </div>
        <div><Label>Notas</Label><Textarea rows={2} value={cur.notas || ""} onChange={(e) => setCur({ ...cur, notas: e.target.value })} data-testid="viaje-notas" /></div>
        <DialogFooter><Button variant="outline" onClick={() => setCur(null)}>Cancelar</Button><Button onClick={guardar} disabled={saving} data-testid="viaje-guardar">{saving ? "Guardando..." : "Guardar viaje"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViajeDetalle({ viaje, onClose }) {
  if (!viaje) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="viaje-detalle">
        <DialogHeader><DialogTitle>Viaje {viaje.folio}</DialogTitle>
          <DialogDescription>{fechaMX(viaje.fecha)} · {viaje.chofer?.full_name} · {viaje.camion ? viaje.camion.placas : "Sin camión"} · {viaje.hechas} de {viaje.total} atendidas</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {viaje.entregas.map((e, i) => (
            <div key={e.id} className="border rounded-md p-3 text-sm" data-testid={`viaje-detalle-entrega-${e.id}`}>
          <div className="flex flex-wrap justify-between gap-2"><span className="font-semibold">{i + 1}. {e.cliente}</span>
                {e.resultado ? <span className="flex gap-1">{e.captura_manual && <Badge variant="outline">manual</Badge>}<Badge className={RESULTADO_CLS[e.resultado]}>{e.resultado}</Badge></span> : <Badge variant="outline">Pendiente</Badge>}</div>
              <div className="text-xs text-muted-foreground">{e.numero_remision} · {e.numero_pedido || "-"} · {e.direccion}</div>
              {e.resultado && <div className="text-xs mt-1">{fechaHoraMX(e.entregado_at)}{e.motivo ? ` · Motivo: ${e.motivo}` : ""}{e.nota ? ` · ${e.nota}` : ""}</div>}
              <ArchivosEntrega entrega={e} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Viajes() {
  const [viajes, setViajes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [camiones, setCamiones] = useState([]);
  const [cur, setCur] = useState(null);
  const [ver, setVer] = useState(null);
  const cargar = () => api.get("/viajes").then(({ data }) => setViajes(data || []));
  useEffect(() => {
    cargar();
    api.get("/choferes").then(({ data }) => setChoferes((data || []).filter((c) => (c.estatus || "activo") === "activo")));
    api.get("/camiones").then(({ data }) => setCamiones(data || []));
  }, []);
  const nuevo = () => setCur({ chofer_id: "", camion_id: "", fecha: hoy(), notas: "", remision_ids: [], pendientes: [] });
  const editar = (v) => {
    const pend = v.entregas.filter((e) => !e.resultado);
    const hechas = v.entregas.filter((e) => e.resultado);
    setCur({ id: v.id, folio: v.folio, chofer_id: v.chofer_id, camion_id: v.camion_id || "", fecha: v.fecha, notas: v.notas, remision_ids: pend.map((e) => e.remision_id), pendientes: pend, hechas: hechas.length, hechasIds: hechas.map((e) => e.remision_id).filter(Boolean) });
  };
  const cancelar = async (v) => {
    if (!window.confirm(v.hechas ? `El viaje ${v.folio} ya tiene entregas registradas. Se quitarán las pendientes y se cerrará. ¿Continuar?` : `¿Eliminar el viaje ${v.folio}?`)) return;
    try { await api.delete(`/viajes/${v.id}`); toast.success("Listo"); cargar(); } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };
  const activos = viajes.filter((v) => v.estatus !== "Completado");
  const cerrados = viajes.filter((v) => v.estatus === "Completado");
  const tabla = (rows, testid) => (
    <div className="rounded-lg border overflow-x-auto" data-testid={testid}>
      <Table>
        <TableHeader><TableRow><TableHead>Viaje</TableHead><TableHead>Fecha</TableHead><TableHead>Chofer</TableHead><TableHead>Camión</TableHead><TableHead>Avance</TableHead><TableHead>Kg</TableHead><TableHead>Estatus</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">Sin viajes</TableCell></TableRow>}
          {rows.map((v) => (
            <TableRow key={v.id} data-testid={`viaje-fila-${v.id}`}>
              <TableCell className="font-semibold">{v.folio}</TableCell><TableCell>{fechaMX(v.fecha)}</TableCell><TableCell>{v.chofer?.full_name}</TableCell>
              <TableCell>{v.camion?.placas || "-"}</TableCell><TableCell data-testid={`viaje-avance-${v.id}`}>{v.hechas} de {v.total}</TableCell><TableCell>{v.kg.toLocaleString("es-MX")}</TableCell>
              <TableCell><Badge className={`${EST_CLS[v.estatus] || ""} border-transparent`}>{v.estatus}</Badge></TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button variant="outline" size="icon" title="Ver" onClick={() => setVer(v)} data-testid={`viaje-ver-${v.id}`}><Eye className="h-4 w-4" /></Button>
                {v.estatus !== "Completado" && <Button variant="outline" size="icon" className="ml-2" title="Editar" onClick={() => editar(v)} data-testid={`viaje-editar-${v.id}`}><Pencil className="h-4 w-4" /></Button>}
                {v.estatus !== "Completado" && <Button variant="outline" size="icon" className="ml-2 text-red-600" title="Cancelar" onClick={() => cancelar(v)} data-testid={`viaje-cancelar-${v.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="viajes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold flex items-center gap-2"><Route className="h-6 w-6" />Viajes</h1><p className="text-sm text-muted-foreground">Arma los fletes y asígnalos a cada chofer</p></div>
        <Button onClick={nuevo} data-testid="viaje-nuevo"><Plus className="h-4 w-4 mr-1" />Nuevo viaje</Button>
      </div>
      <Tabs defaultValue="activos">
        <TabsList><TabsTrigger value="activos" data-testid="tab-viajes-activos">Activos ({activos.length})</TabsTrigger><TabsTrigger value="cerrados" data-testid="tab-viajes-cerrados">Completados</TabsTrigger><TabsTrigger value="historial" data-testid="tab-historial-choferes">Historial por chofer</TabsTrigger></TabsList>
        <TabsContent value="activos">{tabla(activos, "viajes-activos")}</TabsContent>
        <TabsContent value="cerrados">{tabla(cerrados, "viajes-cerrados")}</TabsContent>
        <TabsContent value="historial"><HistorialEntregas choferes={choferes} /></TabsContent>
      </Tabs>
      <ViajeDialog cur={cur} setCur={setCur} choferes={choferes} camiones={camiones} onSaved={cargar} />
      <ViajeDetalle viaje={ver} onClose={() => setVer(null)} />
    </div>
  );
}
